import React from "react";
import { render, hydrate } from "react-dom";
import { createBrowserHistory } from "history";
import { ConnectedRouter } from "connected-react-router";
import { Provider } from "react-redux";
import { createStore, applyMiddleware, compose } from "redux";
import { renderRoutes } from "react-router-config";
import thunk from "redux-thunk";
import { init as initSentry } from "@sentry/browser";
import { loadableReady } from "@loadable/component";
import ReactGA from "react-ga4";
import axios from "axios";
import { IntlProvider } from "react-intl";

import { getItem, setItem, hasItem } from "../cookie";
import getLanguage from "../utils/getLanguage";
import isSupportedBrowser from "../utils/isSupportedBrowser";
import NotSupported from "../components/NotSupported";

import reducers from "./reducers";
import routes from "./routes";
import { IUser } from "../store/user/models";

const history = createBrowserHistory();

if (process.env.NODE_ENV !== "development" && isSupportedBrowser().isSupported === "yes") {
    initSentry({ dsn: "https://28facc69d62b429682ccfaeb36fbd3c1@sentry.io/1515149" });
} else if (process.env.NODE_ENV !== "development") {
    console.warn("Not supported", isSupportedBrowser().supportMessage);
}

const isAdmin = process.env.NODE_ENV === "development" ||
    (window.INITIAL_STATE.user && (window.INITIAL_STATE.user as IUser)._id === "5b2504ca09ddd4002a1d40f3");
if (!isAdmin) {
    if (window.location.host.indexOf(".com") !== -1) {
        ReactGA.initialize("G-Q4PWERC16Q");
        ReactGA.send({ hitType: "pageview", page: window.location.pathname });
    }
}

window.historyList = [];
history.listen((location, action) => {
    if (window.ga && window.ga.create && !isAdmin) {
        ReactGA.send({ hitType: "pageview", page: location.pathname });
    }
    window.historyList.push({ location, action });
    doAnalytics();
});

const axiosInstance = axios.create({
    baseURL: "/api",
});

const axiosRoot = axios.create({
    baseURL: "",
});

let analyticsEnabled = true;
const doAnalytics = async () => {
    if (!analyticsEnabled) return;
    analyticsEnabled = false;
    if (hasItem("aid")) {
        const _aid = getItem("aid");
        const { data } = await axiosInstance.post("/user-analytics", { _aid });
        if (data !== "success") {
            setItem("aid", data, Infinity);
        }
    } else {
        const { data: _aid } = await axiosInstance.post("/user-analytics", { _aid: null });
        setItem("aid", _aid, Infinity);
    }
    setTimeout(() => {
        analyticsEnabled = true;
    }, 5 * 60 * 1000);
};

setTimeout(doAnalytics, 3000);

let language = getLanguage();

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
const store = createStore(
    reducers(history),
    window.INITIAL_STATE,
    composeEnhancers(applyMiddleware(thunk.withExtraArgument(axiosInstance)))
);

const renderMethod = process.env.WEBPACK ? render : hydrate;

const renderPageWithLanguage = (language: string) => {
    setItem("language", language, 60 * 60 * 24 * 365);
    document.getElementsByTagName("html")[0].setAttribute("lang", language);
    const { isSupported, supportMessage } = isSupportedBrowser();
    window.recaptchaOptions = { lang: language, removeOnMount: true };
    if (isSupported === "no") {
        renderMethod(
            <IntlProvider locale={language} key={language}>
                <NotSupported notSupported={supportMessage} />
            </IntlProvider>,
            document.getElementById("root")
        );
    } else {
        const timeago = window.registerLanguageForTimeago(language);
        const languages = axiosRoot.get("/translations/" + language + ".json");
        Promise.all([languages, timeago]).then(([res]) => {
            let messages = res.data;
            loadableReady(() => {
                renderMethod(
                    <Provider store={store}>
                        <IntlProvider locale={language} messages={messages}>
                            <ConnectedRouter history={history}>
                                <div>{renderRoutes(routes as any)}</div>
                            </ConnectedRouter>
                        </IntlProvider>
                    </Provider>,
                    document.getElementById("root")
                );
            });
        });
    }
};

window.renderPageWithLanguage = renderPageWithLanguage;

renderPageWithLanguage(language);

document.addEventListener("dragstart", function (e) {
    if ((event.target as Element).nodeName.toUpperCase() === "IMG") {
        e.preventDefault();
    }
});
